import { useState } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { LinearProgress } from '@material-ui/core'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'
import { Typography } from 'common/typography'
import { SecondaryButton } from 'common/button'
import Wrapper from 'common/wrapper'
import ContentRenderer from 'content-renderer'
import isRecent from 'utils/is-recent'
import { useProject } from 'querys/projects'
import { useStartProject } from 'mutations/projects'
import ProjectTags from 'projects/project-tags'
import { getProjectFileService } from 'services/projects'
import Notification from 'common/notification'
import { AiOutlineDownload } from 'react-icons/ai'
import FreeVideoComponent from 'common/video/free-video-component'
import ProjectChecks from './project-checks'
import DownloadStatus from './download-progress'

const ProjectHeroStyled = styled.div`
  padding: 60px 0 64px;
  .project-description {
    p, li {
      margin: 16px 0;
      color: #8D9DAB;
    }
  }
  .project-hero {
    width: 100%;
  }
  img {
    object-fit: cover;
  }
  .back {
    padding: 24px 0;
    font-weight: 500;
    color: var(--secondary);
    font-size: 16px;
    line-height: 24px;
    margin-left: 8px;
    font-family: var(--secondaryFont);
  }
  h1 {
    margin-bottom: 16px;
  }
  .buy-btns {
    display: grid;
    row-gap: 16px;
  }
  .back-btn {
    display: flex;
    align-items: center;
  }
  p a {
    text-decoration: underline;
  }
  p b {
    color: var(--turquoise);
  }
  #start-free {
    margin-block-start: 8px;
  }
  .revision-status {
    margin: 24px 0;
  }
  .check {
    margin-top: 24px;
    height: 20px;
    display: flex;
    align-items: center;
    user-select: none;
    cursor: pointer;
    input, .checked {
      display: none;
    }
    .checked, .to-check {
      margin-right: 8px;
    }
    input:checked {
      ~ .checked  {
        display: block;
      }
      ~ .to-check  {
        display: none;
      }
    }
  }
  .checkbox {
    margin-right: 10px;
    height: 14px;
    width: 14px;
    border: 2px solid var(--secondary);
    border-radius: 3px;
    transition: 0.2s ease-in-out;
  }
  .project-container {
    display: flex;
    flex-direction: column-reverse;
    gap: 16px;
  }
  @media(min-width: 1024px) {
    padding: 0 0 200px;
    position: relative;
    overflow: hidden;
    .back {
      padding: 48px 0;
    }
    .buy-btns {
      display: grid;
      grid-template-columns: repeat(2, min-content);
      column-gap: 16px;
    }
    .project-hero {
      width: initial;
      position: absolute;
      bottom: 0px;
      right: -36px;
    }
    .project-info {
      max-width: 480px;
    }
    .project-hero {
      display: none;
    }
    .project-hero-lg {
      display: block;
      height: 420px;
      width: 768px;
    }
    .project-container {
      display: grid;
      grid-template-columns: 480px 1fr;
      margin-block-start: 40px;
      column-gap: 24px;
    }
  }
`

function ProjectHero() {
  const [disabled, setDisabled] = useState(true)
  const startProject = useStartProject()
  const {
    downloading, downloaded, setDownloading, setDownloaded, progress, setProgress, error,
  } = startProject
  const {
    data: {
      title, description: { blocks }, cover_image, slug, is_free, level, progress_status, type, created_at, challenge, has_full_access, video,
    },
    token,
  } = useProject()
  // console.log(isFetchedAfterMount, progress_status)
  const handleCheck = async (e) => {
    setDisabled(!e.target.checked)
  }
  const handleDownload = async () => {
    if (disabled) return
    if (!progress_status?.uuid) {
      startProject.mutate()
    } else {
      setDownloading(true)
      await getProjectFileService({ slug, token, setProgress })
      setDownloaded(true)
      setDownloading(false)
    }
  }
  return (
    <ProjectHeroStyled>
      <Wrapper maxWidth="1200">
        <div className="project-container">
          <div className="project-info">
            <div className="project-title">
              <Typography as="h1" data-cy="project-title" small="headline4" large="headline3" color="var(--baseNeutral)">
                {title}
              </Typography>
              <ProjectTags
                isFree={is_free}
                level={level}
                type={type}
                isNew={isRecent({
                  time: created_at,
                  timeDiference: 30,
                  timeUnit: 'dd',
                })}
                challenge={challenge}
              />
            </div>
            <div className="project-description">
              <ContentRenderer chunks={blocks} />
            </div>
            <div className="revision-status">
              {progress_status?.status === 'IN REVIEW' && (
                <Typography>Tu proyecto esta en revisión.</Typography>
              )}
              {progress_status?.status === 'COMPLETED' && (
                <Typography>¡Felicidades! Haz completado este proyecto</Typography>
              )}
            </div>
            {downloading && progress < 100 && (
              <div className="progress">
                <LinearProgress value={progress} variant="determinate" />
              </div>
            )}
            <ProjectChecks />
            <div>
              {!downloading && !downloaded ? (
                <>
                  {token ? (
                    <>
                      <SecondaryButton id="download-project" disabled={disabled} fullWidth fontWeight="500" onClick={() => handleDownload()}>
                        <AiOutlineDownload color="var(--white)" size={24} />
                        Iniciar proyecto
                      </SecondaryButton>
                      <label className="check" htmlFor="accept-terms-projects">
                        <input onChange={handleCheck} type="checkbox" id="accept-terms-projects" />
                        <MdCheckBox className="checked" color="var(--secondary)" size="24px" />
                        <MdCheckBoxOutlineBlank className="to-check" color="var(--secondary)" size="24px" />
                        <Typography color="var(--baseNeutral)">
                            Acepto
                          {' '}
                          <Link href="/terminos">
                            Terminos y condiciones
                          </Link>
                        </Typography>
                      </label>
                    </>
                  ) : (
                    <>
                      <Link href={`/login?next=/proyectos/${slug}`} passHref>
                        <SecondaryButton fullWidth fontWeight="500">
                          <AiOutlineDownload color="var(--white)" size={24} />
                          Iniciar Proyecto Gratis
                        </SecondaryButton>
                      </Link>
                    </>
                  )}
                </>
              ) : <DownloadStatus loading={downloading} />
              }
            </div>
            {error?.message === 'projects.MaxProjectsReached' ? (
              <Notification
                title="Tienes mas de 5 proyectos en curso"
                message="Has alcanzado el numero maximo de proyectos que puedes desarrolar en paralelo."
                error
              />
            ) : null}
          </div>
          <div className="project-hero-lg">
            <FreeVideoComponent
              provider_id={video.provider_id}
              title={title}
              thumb={cover_image}
            />
          </div>
        </div>
      </Wrapper>
    </ProjectHeroStyled>
  )
}

export default ProjectHero
